import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { OnboardingStatus, UserRole } from '../../types/user.types';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRoles?: UserRole[];
  requireCompleteOnboarding?: boolean;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredRoles = [],
  requireCompleteOnboarding = true
}) => {
  const { currentUser, userProfile, loading } = useAuth();
  const location = useLocation();

  // Track if we're waiting for profile to load after auth
  const [waitingForProfile, setWaitingForProfile] = useState(false);

  // When currentUser changes and userProfile is null, start waiting
  useEffect(() => {
    if (currentUser && !userProfile && !loading) {
      setWaitingForProfile(true);
      // Set a timeout to stop waiting after 5 seconds (prevents infinite spinner)
      const timeout = setTimeout(() => {
        setWaitingForProfile(false);
      }, 5000);
      return () => clearTimeout(timeout);
    } else if (userProfile) {
      setWaitingForProfile(false);
    }
    return undefined;
  }, [currentUser, userProfile, loading]);

  // Show loading spinner while auth is loading OR while waiting for profile
  if (loading || (currentUser && !userProfile && waitingForProfile)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (!currentUser) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // If user is authenticated but has no profile after waiting, redirect to login
  if (!userProfile && currentUser) {
    console.warn('User authenticated but no profile found after waiting');
    return <Navigate to="/login" replace />;
  }

  // If user has profile but is not complete and not on onboarding page
  if (userProfile && requireCompleteOnboarding && userProfile.onboardingStatus !== OnboardingStatus.COMPLETE) {
    if (location.pathname !== '/onboarding') {
      return <Navigate to="/onboarding" replace />;
    }
  }

  if (userProfile && requiredRoles.length > 0 && !requiredRoles.includes(userProfile.role)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
};